import {Checkbox, createStyles, Group, Stack, Text, UnstyledButton} from '@mantine/core';
import React from 'react';

import MiniPlateSummary, {
  MiniPlateSummaryFilledPosition,
} from 'notebook/integrations/plate-maps/scratch-plates/MiniPlateSummary';
import {PlateSize} from 'samples/storables/StorableSchemaConstants';
import {FONT_WEIGHT_PROXIMA_REGULAR} from 'styles/FontConstants';
import {RelativeTime} from 'ui-library/Times';

export interface SelectablePlate {
  scratchPlateId: string;
  scratchPlateName: string | null;
  filledPositions: ReadonlyArray<MiniPlateSummaryFilledPosition> | null;
  createdAt: string | null;
}

interface SelectablePlateListProps {
  plates: Array<SelectablePlate>;
  plateSize: PlateSize;
  selectedPlateIds: ReadonlySet<string>;
  onToggleSelected: (scratchPlateId: string) => void;
  onSelectAll: () => void;
  onDeselectAll: () => void;
}

export default function SelectablePlateList({
  plates,
  plateSize,
  selectedPlateIds,
  onToggleSelected,
  onSelectAll,
  onDeselectAll,
}: SelectablePlateListProps): JSX.Element {
  const {classes, cx} = useStyles();
  const [height, width] = plateSize;
  const allSelected =
    plates.length > 0 && plates.every((plate) => selectedPlateIds.has(plate.scratchPlateId));

  return (
    <Stack className={classes.wrapper} spacing={0}>
      <Group className={classes.header} spacing="lg" noWrap={true}>
        <Text className={classes.text}>{selectedPlateIds.size} selected</Text>
        <UnstyledButton
          className={cx(classes.text, classes.selectAll)}
          onClick={allSelected ? onDeselectAll : onSelectAll}
        >
          {allSelected ? 'Deselect all' : 'Select all'}
        </UnstyledButton>
      </Group>
      <Stack className={classes.list} spacing={0}>
        {plates.map((plate) => {
          const displayName = plate.scratchPlateName ?? plate.scratchPlateId;
          return (
            <label key={plate.scratchPlateId} className={classes.row} htmlFor={plate.scratchPlateId}>
              <Checkbox
                id={plate.scratchPlateId}
                checked={selectedPlateIds.has(plate.scratchPlateId)}
                onChange={() => {
                  onToggleSelected(plate.scratchPlateId);
                }}
                aria-label={`Select ${displayName}`}
              />
              {plate.filledPositions != null ? (
                <MiniPlateSummary width={width} height={height} filledPositions={plate.filledPositions} />
              ) : null}
              <Stack spacing={0} className={classes.nameColumn}>
                <Text className={classes.text} truncate={true}>
                  {displayName}
                </Text>
                {plate.createdAt != null && (
                  <Text className={cx(classes.text, classes.createdAt)} truncate={true}>
                    Created <RelativeTime datetime={plate.createdAt} />
                  </Text>
                )}
              </Stack>
            </label>
          );
        })}
      </Stack>
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  text: {
    fontSize: theme.fontSizes.md,
  },
  wrapper: {
    border: `1px solid ${theme.colors.gray[3]}`,
    borderRadius: theme.radius.sm,
  },
  header: {
    padding: theme.spacing.sm,
    backgroundColor: theme.colors.gray[0],
  },
  selectAll: {
    color: theme.colors.blue[6],
    cursor: 'pointer',
  },
  list: {
    maxHeight: 320,
    overflowY: 'auto',
  },
  row: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.sm,
    padding: theme.spacing.sm,
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: theme.colors.gray[0],
    },
  },
  nameColumn: {
    minWidth: 0,
  },
  createdAt: {
    color: theme.colors.gray[6],
    fontWeight: FONT_WEIGHT_PROXIMA_REGULAR,
    fontSize: theme.fontSizes.sm,
  },
}));
